/**
 * Zustand store for authentication state
 * Manages JWT tokens and user info
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { login, register, getCurrentUser, logout, LoginRequest, RegisterRequest, UserResponse } from '../api/authApi';

export interface AuthState {
  // State
  user: UserResponse | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  loginUser: (request: LoginRequest) => Promise<void>;
  registerUser: (request: RegisterRequest) => Promise<void>;
  fetchCurrentUser: (token: string) => Promise<void>;
  logoutUser: () => Promise<void>;
  clearError: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      loginUser: async (request: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          const response = await login(request);
          set({
            user: response.user,
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      registerUser: async (request: RegisterRequest) => {
        set({ isLoading: true, error: null });
        try {
          await register(request);
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchCurrentUser: async (token: string) => {
        set({ isLoading: true, error: null });
        try {
          const user = await getCurrentUser(token);
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch user',
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      logoutUser: async () => {
        set({ isLoading: true, error: null });
        try {
          const token = useAuthStore.getState().accessToken;
          if (token) {
            await logout(token);
          }
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Logout failed',
            isLoading: false,
          });
        }
      },

      clearError: () => set({ error: null }),

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken, isAuthenticated: true });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
